import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { getRevenueAnalytics, getRevenueChartData, convertToChartData, formatCurrency, formatPercentage } from '../services/revenue';
import { getRevenueBreakdown } from '../services/dashboardStats';
import './RevenueChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

interface RevenueDataPoint {
  name: string;
  value: number;
}

interface RevenueChartProps {
  data?: RevenueDataPoint[];
  loading?: boolean;
  totalRevenue?: string;
  currentPeriod?: string;
  currentAmount?: string;
  percentageChange?: number;
  trend?: 'up' | 'down';
  period?: string;
  granularity?: string;
}


const RevenueChart: React.FC<RevenueChartProps> = ({
  data: propData = [],
  loading = false,
  totalRevenue = 'N0',
  currentPeriod = 'No Data',
  currentAmount = 'N0',
  percentageChange = 0,
  trend = 'up',
  period = 'month',
  granularity = 'day'
}) => {
  const [apiData, setApiData] = useState<RevenueDataPoint[]>([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [revenueBreakdown, setRevenueBreakdown] = useState<any>(null);

  // Fetch revenue data from API
  useEffect(() => {
    const fetchRevenueData = async () => {
      setApiLoading(true);
      setApiError(null);
      
      try {
        const [analyticsResponse, chartResponse, breakdownResponse] = await Promise.all([
          getRevenueAnalytics(period, granularity),
          getRevenueChartData('line', period, granularity),
          getRevenueBreakdown(undefined, period, 'hourly')
        ]);

        // Use chart data if available, otherwise convert analytics data
        let chartData: RevenueDataPoint[];
        if (chartResponse.labels && chartResponse.datasets[0]?.data) {
          chartData = chartResponse.labels.map((label, index) => ({
            name: label,
            value: chartResponse.datasets[0].data[index]
          }));
        } else {
          chartData = convertToChartData(analyticsResponse.time_series);
        }

        setApiData(chartData);
        setSummary(analyticsResponse.summary);
        setRevenueBreakdown(breakdownResponse);
        
        // Debug logging
        console.log('Revenue Breakdown API Response:', breakdownResponse);
        console.log('Revenue Analytics API Response:', analyticsResponse);
        console.log('Chart Data:', chartData);
      } catch (error) {
        console.error('Error fetching revenue data:', error);
        setApiError('Failed to load revenue data');
        // Don't fall back to sample data - show empty state
        setApiData([]);
      } finally {
        setApiLoading(false);
      }
    };

    fetchRevenueData();
  }, [period, granularity]);

  // Use API data if available, otherwise fall back to props data
  const displayData = apiData.length > 0 ? apiData : propData;
  const isLoading = loading || apiLoading;
  

  // Calculate average value for the horizontal line
  const averageValue = displayData.length > 0 
    ? displayData.reduce((sum, item) => sum + (item.value || 0), 0) / displayData.length 
    : 0;

  // Use revenue breakdown API data for the top summary section
  const displayTotalRevenue = revenueBreakdown?.summary?.total_revenue ? formatCurrency(revenueBreakdown.summary.total_revenue) : 'N0';
  const displayCurrentAmount = revenueBreakdown?.summary?.total_revenue ? formatCurrency(revenueBreakdown.summary.total_revenue) : 'N0';
  
  // Calculate percentage change from breakdown data (simplified calculation)
  const displayPercentageChange = revenueBreakdown?.summary?.total_revenue ? 
    Math.round((revenueBreakdown.summary.total_revenue / 100000) * 3.4) : 0;
  const displayTrend = revenueBreakdown?.summary?.total_revenue ? 'up' : 'up';
  
  // Debug logging for display values
  console.log('Display Values:', {
    displayTotalRevenue,
    displayCurrentAmount,
    displayPercentageChange,
    displayTrend,
    revenueBreakdown: !!revenueBreakdown
  });

  const formatYAxis = (value: number) => {
    if (value >= 1000000) {
      return `N${(value / 1000000).toFixed(0)}M`;
    } else if (value >= 1000) {
      return `N${(value / 1000).toFixed(0)}K`;
    }
    return `N${value}`;
  };

  if (isLoading) {
    return (
      <div className="revenue-chart">
        <div className="revenue-chart-header">
          <div>
            <h3 className="revenue-chart-title">Revenue Details</h3>
            <div className="revenue-chart-stats">
              <div className="skeleton skeleton-main-value"></div>
              <div className="revenue-chart-details">
                <span className="skeleton skeleton-date"></span>
                <span className="skeleton skeleton-amount"></span>
                <span className="skeleton skeleton-change"></span>
              </div>
            </div>
          </div>
        </div>
        <div className="revenue-chart-container">
          <div className="chart-loading">
            <div className="loading-spinner"></div>
            <span>Loading chart data...</span>
          </div>
        </div>
      </div>
    );
  }

  if (displayData.length === 0) {
    return (
      <div className="revenue-chart">
        <div className="revenue-chart-header">
          <div>
            <h3 className="revenue-chart-title">Revenue Details</h3>
            <div className="revenue-chart-stats">
              <div className="revenue-chart-main-value">{displayTotalRevenue}</div>
              <div className="revenue-chart-details">
                <span className="revenue-chart-date">{revenueBreakdown?.period?.date ?? 'No Data'}</span>
                <span className="revenue-chart-amount">{displayCurrentAmount}</span>
                <span className={`revenue-chart-change ${displayTrend}`}>
                  {displayPercentageChange > 0 ? '+' : ''}{displayPercentageChange}%
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="revenue-chart-container">
          <div className="chart-empty">
            <p>No revenue data available. Please check your API connection.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="revenue-chart">
      <div className="revenue-chart-header">
        <div>
          <h3 className="revenue-chart-title">Revenue Details</h3>
          <div className="revenue-chart-stats">
            <div className="revenue-chart-main-value">{displayTotalRevenue}</div>
            <div className="revenue-chart-details">
              <span className="revenue-chart-date">{revenueBreakdown?.period?.date ?? currentPeriod}</span>
              <span className="revenue-chart-amount">{displayCurrentAmount}</span>
              <span className={`revenue-chart-change ${displayTrend}`}>
                {displayPercentageChange > 0 ? '+' : ''}{displayPercentageChange}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {apiError && (
        <div className="revenue-chart-error" style={{ 
          padding: '10px', 
          margin: '10px', 
          backgroundColor: '#fee2e2', 
          color: '#991b1b', 
          borderRadius: '4px',
          fontSize: '14px'
        }}>
          <p>{apiError}</p>
        </div>
      )}

      <div className="revenue-chart-container">
        <div style={{ width: '100%', height: '100%' }}>
          <div style={{ width: '100%', height: '350px', padding: '20px' }}>
            <Line
              data={{
                labels: displayData.map(item => item.name),
                datasets: [
                  {
                    label: 'Revenue',
                    data: displayData.map(item => item.value),
                    borderColor: '#14b8a6',
                    backgroundColor: 'rgba(20, 184, 166, 0.1)',
                    borderWidth: 3,
                    fill: true,
                    tension: 0.4,
                    pointBackgroundColor: '#14b8a6',
                    pointBorderColor: '#14b8a6',
                    pointRadius: 4,
                    pointHoverRadius: 6,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false,
                  },
                  tooltip: {
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    titleColor: '#374151',
                    bodyColor: '#374151',
                    borderColor: '#e5e7eb',
                    borderWidth: 1,
                    cornerRadius: 8,
                    displayColors: false,
                    callbacks: {
                      label: function(context) {
                        return `N${Number(context.parsed.y).toLocaleString()}`;
                      },
                      title: function(context) {
                        return `${context[0].label} 00:00`;
                      }
                    }
                  },
                },
                scales: {
                  x: {
                    grid: {
                      display: false,
                    },
                    ticks: {
                      color: '#6b7280',
                      font: {
                        size: 12,
                      },
                    },
                  },
                  y: {
                    grid: {
                      color: '#f0f0f0',
                    },
                    border: {
                      display: false,
                    },
                    ticks: {
                      color: '#6b7280',
                      font: {
                        size: 12,
                      },
                      callback: function(value) {
                        return formatYAxis(Number(value));
                      }
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueChart;
